import { captureError, addBreadcrumb } from "@/utils/sentry";

const shouldLog = import.meta.env.DEV || import.meta.env.VITE_QA_MODE === "true";
const LOG_BUFFER_LIMIT = 50;
const MAX_LOG_ENTRY_LENGTH = 500;

export type ClientLogLevel = "debug" | "info" | "warn" | "error";

export interface ClientLogEntry {
  timestamp: string;
  level: ClientLogLevel;
  message: string;
}

const recentClientLogs: ClientLogEntry[] = [];

function stringifyLogArg(arg: unknown) {
  let formatted: string;

  if (typeof arg === "string") {
    formatted = arg;
  } else if (typeof arg === "number" || typeof arg === "boolean" || arg === null || arg === undefined) {
    formatted = String(arg);
  } else if (arg instanceof Error) {
    formatted = `${arg.name}: ${arg.message}`;
  } else {
    try {
      formatted = JSON.stringify(arg);
    } catch {
      formatted = Object.prototype.toString.call(arg);
    }
  }

  return formatted.length > MAX_LOG_ENTRY_LENGTH
    ? `${formatted.slice(0, MAX_LOG_ENTRY_LENGTH)}...`
    : formatted;
}

function recordClientLog(level: ClientLogLevel, args: unknown[]) {
  const message = args.map(stringifyLogArg).join(" ").trim();
  if (!message) return;

  recentClientLogs.push({
    timestamp: new Date().toISOString(),
    level,
    message,
  });

  if (recentClientLogs.length > LOG_BUFFER_LIMIT) {
    recentClientLogs.splice(0, recentClientLogs.length - LOG_BUFFER_LIMIT);
  }
}

export function getRecentClientLogs() {
  return recentClientLogs.map((entry) => ({ ...entry }));
}

export function clearRecentClientLogsForTest() {
  recentClientLogs.splice(0, recentClientLogs.length);
}

export const logger = {
  debug: (...args: unknown[]) => {
    recordClientLog("debug", args);
    if (shouldLog) {
      console.log(...args);
      addBreadcrumb(String(args[0]), "debug");
    }
  },
  info: (...args: unknown[]) => {
    recordClientLog("info", args);
    if (shouldLog) {
      console.log(...args);
      addBreadcrumb(String(args[0]), "info");
    }
  },
  warn: (...args: unknown[]) => {
    recordClientLog("warn", args);
    if (shouldLog) {
      console.warn(...args);
      addBreadcrumb(String(args[0]), "warning");
    }
  },
  error: (...args: unknown[]) => {
    recordClientLog("error", args);
    console.error(...args);
    // Always send errors to Sentry regardless of dev mode
    const error = args[0];
    captureError(error instanceof Error ? error : new Error(String(error)));
  },
};
